import React, { useEffect, useRef, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { supabase } from '@/integrations/supabase/client';
import { shouldEnableRealtime } from '@/utils/realtimeGuard';
import { addDestinationMarkers } from './GoogleDestinationMapUtils';

declare global {
  interface Window {
    google: typeof google;
  }
}

interface Destination {
  id: string;
  name: string;
  status: string;
  date_created: string;
}

const GoogleDestinationMap: React.FC = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [loading, setLoading] = useState(true);
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);

useEffect(() => {
  fetchDestinations();
  
  // Set up real-time subscription (guarded)
  let channel: any;
  if (shouldEnableRealtime()) {
    channel = supabase
      .channel('destinations-changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'destinations'
      }, () => {
        fetchDestinations();
      })
      .subscribe();
  }

  return () => {
    if (channel) supabase.removeChannel(channel);
  };
}, []);

  useEffect(() => {
    if (destinations.length > 0) {
      initMap();
    }
  }, [destinations]);

  const fetchDestinations = async () => {
    try {
      const { data, error } = await supabase
        .from('destinations')
        .select('*')
        .order('date_created', { ascending: false });

      if (error) throw error;
      setDestinations(data || []);
    } catch (error) {
      console.error('Error fetching destinations:', error);
    } finally {
      setLoading(false);
    }
  };

  const initMap = async () => {
    try {
      // Get API key from edge function using centralized client
      const { data, error } = await supabase.functions.invoke('get-google-maps-key');
      
      if (error) {
        console.error('Error getting Google Maps API key:', error);
        return;
      }
      
      const apiKey = data?.apiKey;

      const loader = new Loader({
        apiKey: apiKey,
        version: 'weekly',
        libraries: ['places'],
      });

      const { Map } = await loader.importLibrary('maps');

      if (!mapRef.current) return;

      const map = new Map(mapRef.current, {
        zoom: 2,
        center: { lat: 20, lng: 0 },
        // Remove mapId to allow custom styles, or remove styles to use mapId
        styles: [
          {
            featureType: 'water',
            elementType: 'geometry',
            stylers: [{ color: '#e9f3ff' }],
          },
          {
            featureType: 'landscape',
            elementType: 'geometry',
            stylers: [{ color: '#f5f5f5' }],
          },
        ],
      });

      setMapInstance(map);

      // Add markers for each destination using geocoding
      await addDestinationMarkers(map, destinations, markersRef);
    } catch (error) {
      console.error('Error initializing Google Maps:', error);
      // Fallback to simple map display
    }
  };

  if (loading) {
    return (
      <div className="h-96 w-full flex items-center justify-center bg-muted rounded-lg">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="relative h-96 w-full rounded-lg overflow-hidden shadow-lg">
      <div ref={mapRef} className="w-full h-full" />
      
      {/* Controls */}
      <div className="absolute bottom-4 right-4 flex flex-col gap-2">
        <div className="bg-white rounded-lg px-3 py-2 shadow-lg text-sm">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
            <span>Open</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
            <span>Closed</span>
          </div>
        </div>
      </div>

      {/* Title */}
      <div className="absolute top-4 left-4 bg-white rounded-lg px-4 py-2 shadow-lg">
        <h3 className="font-semibold text-foreground">Destinations Map</h3>
        <p className="text-sm text-muted-foreground">{destinations.length} destinations</p>
      </div>
    </div>
  );
};

export default GoogleDestinationMap;